import { useContext, useRef, useState, useMemo, useEffect } from "react";
import LoadProgress from "../../global/Loading";
import ChartSkeleton from "../../global/ChartSkeleton";
import { reportCardHeight, forgetCardHeight, CARD_HEIGHT_VARIABLE } from "../../utils/cardHeight";
import NoData from "../ErrorMessage";
import {
    Box,
    Tabs,
    Tab,
    AppBar,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import MetaData from "./Metadata";
import { FiltersContext } from "../../store/filters-options-context";
import BarChartIcon from "@mui/icons-material/BarChart";
import TableViewIcon from "@mui/icons-material/TableView";
import { useIntersectionObservers } from "../../utils/intersectionObservers";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import styled from "@emotion/styled";
import ItemTitle from "./ItemTitle";
import { useIndicatorData } from "../../hooks/useIndicatorData";
import { useTabControls } from "../../hooks/useTabControls";
import { usePopoverControls } from "../../hooks/usePopoverControls";
import { useIndicatorControls } from "../../hooks/useIndicatorControls";
import { checkNoResults } from "../../utils/indicatorUtils";
import IndicatorControls from "./IndicatorControls";
import MobilePopover from "./MobilePopover";
import TabPanel from "./TabPanel";
import ChartTabContent from "./ChartTabContent";
import DataTableTabContent from "./DataTableTabContent";

const StyledTabs = styled((props) => (
    <Tabs
        {...props}
        TabIndicatorProps={{ children: <span className="MuiTabs-indicatorSpan" /> }}
    />
))({
    "& .MuiTabs-indicator": {
        height: "5px",
    },
    "& .MuiTabs-indicatorSpan": {
        width: "100%",
    },
});

export default function LazyLoadItem({
    url,
    info,
    ChartComponent,
    title,
    itemId,
    renderOrder,
    targetOrder,
    item_code,
    element_code,
    country,
    countryCode,
    dataSource,
    chartTitle,
    regions,
    isPoU,
    isFoodInsecurity,
    domain_code,
    onLoadingStart,
    onLoadingEnd,
    initialAnchor,
    perChartLoading = true
}) {
    const { setActiveMenu, activeMenu, params, areaGroups } = useContext(FiltersContext);
    const [isVisible, setIsVisible] = useState(false);
    const ref = useRef();
    const refDownload = useRef();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    // Derive comparingCountries from params.area and areaGroups (same logic as FixedHeader)
    const comparingCountries = useMemo(() => {
        if (!params?.area || !Array.isArray(params.area) || !areaGroups || areaGroups.length === 0) {
            return [];
        }

        // Skip the first element (main country) and get comparison countries
        const comparisonCodes = params.area.slice(1);

        // Map codes to area group objects
        return comparisonCodes
            .map(code => areaGroups.find(area => area.code === code))
            .filter(Boolean); // Remove any undefined values
    }, [params?.area, areaGroups]);

    // Custom hooks for state management
    const { data, originalData, relatedLinks, isLoading, error, fetchComplete } = useIndicatorData(isVisible, url, info, countryCode, itemId, country, dataSource, comparingCountries);
    const { tab, handleChangeTab } = useTabControls();
    const { anchorEl, open, id, handleClose, handleToggle } = usePopoverControls();
    const { showDataLabels, showSubregions, useLogScale, toggleDataLabels, toggleSubregions, toggleLogScale } = useIndicatorControls();

    if (info && info.exclude_subregional_comparison) {
        // If the indicator excludes subregional comparison, always hide subregions
        if (!showSubregions) {
            toggleSubregions();
        }
    }

    useIntersectionObservers(ref, setIsVisible, setActiveMenu);

    // Force load charts up to the initial anchor target (by render order) to prevent scroll interruption
    useEffect(() => {
        if (initialAnchor != null && targetOrder != null && renderOrder != null) {
            if (renderOrder <= targetOrder) {
                setIsVisible(true);
            }
        }
    }, [initialAnchor, targetOrder, renderOrder]);

    // Notify parent (CountryDetail) of loading lifecycle only on state transitions
    const notifyStateRef = useRef({ started: false, ended: false });

    // Reset notification flags when the underlying query target changes
    useEffect(() => {
        notifyStateRef.current = { started: false, ended: false };
    }, [url, countryCode, itemId]);

    useEffect(() => {
        // Start notification
        if (isVisible && isLoading && !notifyStateRef.current.started) {
            notifyStateRef.current.started = true;
            notifyStateRef.current.ended = false;
            if (typeof onLoadingStart === 'function') onLoadingStart();
        }
        // End notification
        if (
            isVisible &&
            !isLoading &&
            fetchComplete &&
            notifyStateRef.current.started &&
            !notifyStateRef.current.ended
        ) {
            notifyStateRef.current.ended = true;
            if (typeof onLoadingEnd === 'function') onLoadingEnd();
        }
    }, [isVisible, isLoading, fetchComplete, onLoadingStart, onLoadingEnd]);

    const noData = checkNoResults(fetchComplete, data, originalData, info, country);

    // Only a card that drew a chart says anything about how tall a chart card is: a "no data"
    // message or an error box is much shorter and would drag the average down.
    const hasRenderedChart = fetchComplete && !isLoading && !error && !noData;

    useEffect(() => {
        const element = ref.current;
        if (!hasRenderedChart || !element || typeof ResizeObserver === "undefined") return undefined;

        const observer = new ResizeObserver(([entry]) => {
            reportCardHeight(itemId, entry.target.getBoundingClientRect().height);
        });
        observer.observe(element);

        return () => {
            observer.disconnect();
            forgetCardHeight(itemId);
        };
    }, [hasRenderedChart, itemId]);

    const getClassName = () =>
        String(activeMenu) === String(itemId) ? "active-item" : "";

    return (
        <Box
            ref={ref}
            className={`single-item ${getClassName()} ${noData ? "no-results-container" : ""
                }`}
            id={itemId}
            sx={{
                // Until the chart is drawn, reserve the height of an average rendered card, so
                // cards filling in while you scroll past do not push the rest of the page down.
                // The fallbacks are the previous minimums, used before any card has rendered on
                // this viewport size.
                minHeight: hasRenderedChart
                    ? "40vh"
                    : {
                        xs: `var(${CARD_HEIGHT_VARIABLE}, 40vh)`,
                        md: `var(${CARD_HEIGHT_VARIABLE}, max(40vh, 475px))`,
                    },
            }}
        >
            {(!isLoading && !fetchComplete) ? (
                // Defer rendering charts until the first fetch cycle starts or completes to avoid flashing empty charts
                null
            ) : isLoading ? (
                perChartLoading ? (
                    // Everything above the chart is static props, so render it for real and
                    // let only the chart area pulse.
                    <>
                        <div className="item-title">
                            <ItemTitle info={info} title={title} dataSource={dataSource} />
                        </div>
                        <ChartSkeleton />
                    </>
                ) : (
                    <LoadProgress isFetching={isLoading} />
                )
            ) : error ? (
                <NoData text={error} severity="error" />
            ) : (
                <>
                    <div className="item-title">
                        <ItemTitle info={info} title={title} dataSource={dataSource} />
                    </div>

                    <Box
                        display="flex"
                        justifyContent="space-between"
                        sx={{ flexDirection: { xs: "column", lg: "row" }, gap: "1.2vw" }}
                    >
                        <Box
                            className="tabs-container"
                            sx={{
                                border: (theme) => `1px solid ${theme.palette.secondary.dark}`,
                                backgroundColor: noData ? null : "secondary.light",
                                width: "100%",
                            }}
                        >
                            <AppBar
                                position="relative"
                                color="default"
                                elevation={0}
                                className="app-bar"
                                sx={{
                                    backgroundColor: "secondary.dark",
                                }}
                            >
                                <MoreHorizIcon
                                    color="primary"
                                    onClick={handleToggle}
                                    fontSize="small"
                                    sx={{
                                        position: "absolute",
                                        right: "14px",
                                        top: "14px",
                                        zIndex: "99",
                                        display: { sx: "block", sm: "none" },
                                        border: (theme) =>
                                            `1px solid ${theme.palette.primary.main}`,
                                        borderRadius: "30px",
                                    }}
                                />
                                {isMobile ? (
                                    <MobilePopover
                                        id={id}
                                        open={open}
                                        anchorEl={anchorEl}
                                        handleClose={handleClose}
                                        info={info}
                                        item_code={item_code}
                                        data={data}
                                        showDataLabels={showDataLabels}
                                        useLogScale={useLogScale}
                                        toggleLogScale={toggleLogScale}
                                        showSubregions={showSubregions}
                                        toggleDataLabels={toggleDataLabels}
                                        toggleSubregions={toggleSubregions}
                                        originalData={originalData}
                                        refDownload={refDownload}
                                        title={info?.label}
                                        country={country}
                                        params={params}
                                        currentTab={tab}
                                    />
                                ) : (
                                    <Box className="tabs-buttons">
                                        <IndicatorControls
                                            info={info}
                                            item_code={item_code}
                                            data={data}
                                            showDataLabels={showDataLabels}
                                            useLogScale={useLogScale}
                                            toggleLogScale={toggleLogScale}
                                            showSubregions={showSubregions}
                                            toggleDataLabels={toggleDataLabels}
                                            toggleSubregions={toggleSubregions}
                                            originalData={originalData}
                                            refDownload={refDownload}
                                            title={info.label}
                                            country={country}
                                            params={params}
                                            currentTab={tab}
                                        />
                                    </Box>
                                )}
                                <StyledTabs
                                    value={tab}
                                    onChange={handleChangeTab}
                                    aria-label="tabs"
                                >
                                    <Tab
                                        icon={<BarChartIcon sx={{ fontSize: "20px" }} />}
                                        iconPosition="end"
                                        label="CHART"
                                        style={{ fontSize: isMobile ? "10px" : "12px" }}
                                    />
                                    <Tab
                                        icon={<TableViewIcon sx={{ fontSize: "20px" }} />}
                                        iconPosition="end"
                                        label="DATA TABLE"
                                        style={{ fontSize: isMobile ? "10px" : "12px" }}
                                    />
                                </StyledTabs>
                            </AppBar>

                            <Box display="flex" sx={{ width: "100%" }} ref={refDownload}>
                                <TabPanel value={tab} index={0}>
                                    <ChartTabContent
                                        noData={noData}
                                        isPoU={isPoU}
                                        isFoodInsecurity={isFoodInsecurity}
                                        header={info?.header}
                                        footer={info?.footer}
                                        ChartComponent={ChartComponent}
                                        query={url}
                                        data={data}
                                        showDataLabels={showDataLabels}
                                        useLogScale={useLogScale}
                                        showSubregions={showSubregions}
                                        chartTitle={chartTitle}
                                        regions={regions}
                                        info={info}
                                    />
                                </TabPanel>

                                <TabPanel value={tab} index={1}>
                                    <DataTableTabContent
                                        noData={noData}
                                        data={data}
                                        title={title}
                                        dataSource={dataSource}
                                    />
                                </TabPanel>
                            </Box>
                        </Box>

                        <MetaData
                            descr={
                                info.description
                                    ? info.description
                                    : originalData?.[0]?.item_description
                            }
                            dataSource={dataSource}
                            relatedLinks={relatedLinks}
                            itemId={itemId}
                            itemDesc={title}
                            isActive={getClassName}
                            domain_code={domain_code}
                            indicatorInfo={info}
                        />
                    </Box>
                </>
            )}
        </Box>
    );
}
